import { API_BASE_URL } from '../config/api';
/**
 * Spoken responses for the global voice assistant.
 *
 * Respects the user's saved Voice Settings:
 *  - voice_mode === 'silent'  -> never speak
 *  - voice_mode === 'basic'   -> speak shortened confirmations
 *  - voice_provider 'elevenlabs' -> use the backend /voice/speech endpoint
 *  - otherwise (or on failure) -> fall back to the browser SpeechSynthesis API
 */

import { speakWithBuiltinBrowserVoice } from './builtinBrowserVoice';
import { getSpokenMessage, VoiceMode } from './voiceModeMessages';
import { getVoiceSettings, VoiceSettingsSnapshot } from './voiceSettingsStore';

let currentAudio: HTMLAudioElement | null = null;

/** Stop any in-flight assistant speech (audio element or browser synthesis). */
export const stopSpeaking = (): void => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = '';
    currentAudio = null;
  }
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

const speakWithBrowser = (text: string, langCode: string, selectedVoiceId?: string): void => {
  speakWithBuiltinBrowserVoice(text, selectedVoiceId, langCode);
};

const playElevenLabsSpeech = async (
  spokenText: string,
  settings: VoiceSettingsSnapshot
): Promise<void> => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  const voiceId = settings.custom_voice_id || settings.elevenlabs_voice_id;
  const lang = settings.default_language || 'en';

  const response = await fetch(`${API_BASE_URL}/voice/speech`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      text: spokenText,
      voice_id: voiceId || undefined,
      language: lang,
    }),
  });

  if (!response.ok) throw new Error(`Speech request failed: ${response.status}`);

  const data = await response.json();
  const audioUrl = data?.audio_url;
  if (!audioUrl) throw new Error('No audio returned');

  const audio = new Audio(audioUrl);
  currentAudio = audio;
  audio.onended = () => {
    if (currentAudio === audio) currentAudio = null;
  };
  await audio.play();
};

/**
 * Speak an assistant response. No-op in silent mode or for empty text.
 * Never throws — TTS failures must not break the conversation flow.
 */
export const speakResponse = async (text: string): Promise<void> => {
  const settings = await getVoiceSettings();
  const spokenText = getSpokenMessage(
    text,
    settings.voice_mode as VoiceMode | undefined,
    settings.default_language
  );
  if (!spokenText) return;

  stopSpeaking();

  const lang = settings.default_language || 'en';

  if (settings.voice_provider !== 'elevenlabs' && settings.voice_provider !== 'custom') {
    speakWithBrowser(spokenText, lang, settings.selected_voice);
    return;
  }

  const hasElevenLabsKey =
    Boolean(settings.elevenlabs_api_key?.trim()) || Boolean(settings.has_elevenlabs_api_key);
  if (!hasElevenLabsKey) {
    speakWithBrowser(spokenText, lang, settings.selected_voice);
    return;
  }

  try {
    await playElevenLabsSpeech(spokenText, settings);
  } catch (err) {
    console.warn('ElevenLabs TTS failed, falling back to browser speech:', err);
    speakWithBrowser(spokenText, lang, settings.selected_voice);
  }
};
